import { TFunction } from 'i18next';

/**
 * Ceilings of the numeric import columns, and the reading of the error the
 * backend answers when a value passes them anyway.
 *
 * The import writes the whole file in one transaction, so a value the column
 * cannot hold does not cost the row - it costs the import. A `roundLitersTo`
 * of 150 (`RundenAuf` is `decimal(5,3)`, so 99.999 is the ceiling) aborted a
 * two-article import with a raw `Out of range value for column 'RundenAuf' at
 * row 1` and wrote nothing (WA-3899).
 *
 * `NumericField` caps what can be typed at 10_000_000 by default, which is far
 * above what these columns take, and no import column passed a `max` of its
 * own. The entries below are the columns' own limits (`decimal(p,s)` holds up
 * to `10^(p-s) - 10^-s`) and cover the fields whose ceiling is BELOW that
 * default - the rest are already out of reach.
 *
 * `precision` travels with the maximum and is not decoration: `NumericField`
 * rounds to the company's `decimalPlacesCount` (2 by default) when the field is
 * left, so a `max` of 99.999 would be handed on as 100 - back over the ceiling
 * it was supposed to hold. Pinning it to the scale of the column both prevents
 * that and lets the third decimal the column has actually be typed.
 */

export interface ImportColumnLimit {
  max: number;
  precision: number;
}

const decimalColumn = (precision: number, scale: number): ImportColumnLimit => ({
  max: Number((10 ** (precision - scale) - 10 ** -scale).toFixed(scale)),
  precision: scale,
});

/** FakturaAssistArtikel. */
export const ARTICLE_IMPORT_COLUMN_LIMITS: Record<string, ImportColumnLimit> = {
  quantityPerLot: decimalColumn(10, 3), // QuantityPerLot decimal(10,3)
  doughWeightBeforeBaking: decimalColumn(8, 3), // Teigeinlage decimal(8,3)
  weightOfLiterOfDough: decimalColumn(8, 3), // FaktorLtKg decimal(8,3)
  roundLitersTo: decimalColumn(5, 3), // RundenAuf decimal(5,3)
  doughSplittingDough1Amount: decimalColumn(10, 3), // TeigSplittingTeig1 decimal(10,3)
};

/**
 * FakturaAssistKunde - every percent column here is `decimal(5,2)`.
 *
 * `discountOnGroup1..20` are not listed: the grid already caps them at 100,
 * which is inside the column at any precision.
 */
export const CUSTOMER_IMPORT_COLUMN_LIMITS: Record<string, ImportColumnLimit> = {
  returnLimit: decimalColumn(5, 2), // ReturnLimit decimal(5,2)
  skonto: decimalColumn(5, 2), // SkontoInProzent decimal(5,2)
  caSpecialDiscount: decimalColumn(5, 2), // SpecialCADiscount decimal(5,2)
};

/** One rejected value, as `assertNumericValuesInRange` reports it. */
export interface OutOfRangeValue {
  /** `articleNo` / `customerNo` of the row it sits in. */
  recordNo: string;
  field: string;
  /** DB column - only for support, never shown. */
  column: string;
  value: number;
  min: number;
  max: number;
}

/**
 * The backend sends the rejected values in `linkedRecords` (a free-form
 * extension each feature shapes for itself). Anything else that arrives there
 * is not this error and is left to the generic handling.
 */
export const parseOutOfRangeValues = (linkedRecords: unknown): OutOfRangeValue[] => {
  const values = (linkedRecords as { outOfRangeValues?: unknown } | undefined)?.outOfRangeValues;

  if (!Array.isArray(values)) {
    return [];
  }

  return values.filter(
    (value): value is OutOfRangeValue =>
      Boolean(value) && typeof value.field === 'string' && typeof value.value === 'number',
  );
};

/** Column header of an import field, so the alert names what the operator sees. */
export type ImportFieldLabels = Record<string, (t: TFunction) => string>;

export const ARTICLE_IMPORT_FIELD_LABELS: ImportFieldLabels = {
  articleNo: (t) => t('common.article_no'),
  description: (t) => t('common.description'),
  eanCode: (t) => t('article.ean_code'),
  quantityPerLot: (t) => t('article.piece_or_unit'),
  doughWeightBeforeBaking: (t) => t('article.dough_weight_before_baking'),
  weightOfLiterOfDough: (t) => t('article.weight_of_liter_of_dough'),
  roundLitersTo: (t) => t('article.round_to'),
  doughSplittingDough1Amount: (t) => t('article.kg_pro_charge'),
};

export const CUSTOMER_IMPORT_FIELD_LABELS: ImportFieldLabels = {
  customerNo: (t) => t('common.customer_no'),
  salutation: (t) => t('customer.salutation'),
  name: (t) => t('common.name'),
  surname: (t) => t('customer.surname'),
  internalName: (t) => t('customer.internal_name'),
  addition: (t) => t('customer.additive'),
  street: (t) => t('customer.street'),
  postBox: (t) => t('customer.post_box'),
  zipCode: (t) => t('customer.zip_code'),
  city: (t) => t('customer.city'),
  country: (t) => t('customer.country'),
  contactPerson: (t) => t('customer.contact_person'),
  phoneBusiness: (t) => t('customer.phone_business'),
  phoneBusiness2: (t) => `${t('customer.phone_business')} 2`,
  phonePrivate: (t) => t('customer.phone_private'),
  phoneMobile: (t) => t('customer.mobile'),
  fax: (t) => t('customer.fax'),
  website: (t) => t('customer.website'),
  deliveryAddressSalutation: (t) => t('customer.salutation'),
  deliveryAddressName: (t) => t('common.name'),
  deliveryAddressAddition: (t) => t('customer.additive'),
  deliveryAddressStreet: (t) => t('customer.street'),
  deliveryAddressPostBox: (t) => t('customer.post_box'),
  deliveryAddressZipCode: (t) => t('customer.zip_code'),
  deliveryAddressCity: (t) => t('customer.city'),
  deliveryAddressCountry: (t) => t('customer.country'),
  costCenter: (t) => t('customer.costCenter'),
  incomeAccount: (t) => t('customer.incomeAccount'),
  slipReferenceNo: (t) => t('customer.slipReference'),
  eBillAccount: (t) => t('customer.e_bill_account_id'),
  tourPreparationArea: (t) => t('customer.setUpStation'),
  tourSatPreparationArea: (t) => t('customer.setUpStationSat'),
  tourSunPreparationArea: (t) => t('customer.setUpStationSun'),
  tourDepartureTime: (t) => t('customer.deliveryTime'),
  tourSatDepartureTime: (t) => `${t('customer.deliveryTime')} ${t('common.sat')}`,
  tourSunDepartureTime: (t) => `${t('customer.deliveryTime')} ${t('common.sun')}`,
  ...Object.fromEntries(
    [1, 2].flatMap((bank) => [
      [`bank${bank}Name`, (t: TFunction) => `${t('customer.bankName')} ${bank}`],
      [`bank${bank}BIN`, (t: TFunction) => `${t('customer.bin')} ${bank}`],
      [`bank${bank}AccountNo`, (t: TFunction) => `${t('customer.accountNo')} ${bank}`],
      [`bank${bank}IBAN`, (t: TFunction) => `${t('customer.iban')} ${bank}`],
      [`bank${bank}BIC`, (t: TFunction) => `${t('customer.bic')} ${bank}`],
      [`bank${bank}MandatNo`, (t: TFunction) => `${t('customer.mandatNumber')} ${bank}`],
    ]),
  ),
  ...Object.fromEntries(
    Array.from({ length: 20 }, (_, index) => [
      `discountOnGroup${index + 1}`,
      (t: TFunction) => `${t('customer.discountPercent')} ${index + 1}`,
    ]),
  ),
  returnLimit: (t) => t('customer.returnLimitIn'),
  skonto: (t) => t('customer.cashDiscountIn'),
  caSpecialDiscount: (t) => `${t('common.discount')} CA`,
};

/** How many values the alert spells out before it starts counting. */
const LISTED_VALUES = 3;

/**
 * `Runden auf = 150 ("TEST2", max. 99.999)` - the field as it is labelled in
 * the grid, the value that was refused, the row it is in, and the ceiling.
 */
export const formatOutOfRangeDetails = (
  values: OutOfRangeValue[],
  labels: ImportFieldLabels,
  t: TFunction,
): string => {
  const listed = values
    .slice(0, LISTED_VALUES)
    .map((value) => {
      const label = labels[value.field]?.(t) ?? value.field;
      const recordNo = value.recordNo ? ` ("${value.recordNo}"` : ' (';
      return `${label} = ${value.value}${recordNo}, max. ${value.max})`;
    })
    .join('; ');
  const rest = values.length - LISTED_VALUES;

  return rest > 0 ? `${listed}; +${rest}` : listed;
};

/** Rows an out-of-range value was found in, for the red row marking. */
export const outOfRangeRecordNos = (values: OutOfRangeValue[]): Set<string> =>
  new Set(values.map(({ recordNo }) => recordNo).filter(Boolean));
